import { memo, useState } from "react";
import { Box, Drawer, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { BookedTeleconsultoriaRowCard } from "../../provider/components/BookedTeleconsultoriaRowCard";
import { useTeleconsultorRowActions } from "../../provider/hooks/useTeleconsultorRowActions";
import { ActivitySectionFrame } from "../../provider/components/ActivitySectionFrame";
import { TeleconsultoriaDateFilter } from "../../shared/components/TeleconsultoriaDateFilter";
import { useTeleconsultoriaDateRange } from "../../shared/hooks/useTeleconsultoriaDateRange";
import { useAgendaDetails } from "../hooks/useAgendaDetails";
import type { AgendaDetailsTab, TeleconsultoriaAgendaSummary } from "../types";
import { AgendaSlotsList } from "./AgendaSlotsList";

const TAB_LABEL: Record<AgendaDetailsTab, string> = {
  slots: "Horários",
  upcoming: "Próximas",
  done: "Realizadas",
};
const EMPTY_TEXT: Record<AgendaDetailsTab, string> = {
  slots: "Nenhum horário neste período.",
  upcoming: "Nenhuma consulta marcada neste período.",
  done: "Nenhuma consulta realizada neste período.",
};

/**
 * Painel lateral da agenda (abre sem sair da página): Horários disponíveis,
 * Próximas e Realizadas, cada uma no período do filtro de datas.
 */
export const TeleconsultoriaScheduleDrawer = memo(function TeleconsultoriaScheduleDrawer({
  agenda,
  onClose,
}: {
  agenda: TeleconsultoriaAgendaSummary | null;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<AgendaDetailsTab>("slots");
  const range = useTeleconsultoriaDateRange("day");
  const { slots, appointments, isLoading, hasError, reload } = useAgendaDetails({ agenda, tab, range });
  const { canStart, pendingInviteOf } = useTeleconsultorRowActions();
  const isSlotsTab = tab === "slots";

  return (
    <Drawer anchor="right" open={Boolean(agenda)} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", sm: 480 }, p: 2 }} role="dialog" aria-label="Detalhes da agenda">
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              {agenda?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {agenda?.practitioner?.display}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} aria-label="Fechar">
            <Close />
          </IconButton>
        </Stack>
        <Tabs
          value={tab}
          onChange={(_, next: AgendaDetailsTab) => setTab(next)}
          variant="fullWidth"
          sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 600 } }}
        >
          {(Object.keys(TAB_LABEL) as AgendaDetailsTab[]).map((key) => (
            <Tab key={key} value={key} label={TAB_LABEL[key]} />
          ))}
        </Tabs>
        <ActivitySectionFrame
          title={TAB_LABEL[tab]}
          toolbar={<TeleconsultoriaDateFilter range={range} />}
          isLoading={isLoading}
          hasError={hasError}
          errorText="Não foi possível carregar esta aba."
          isEmpty={isSlotsTab ? !slots.length : !appointments.length}
          emptyText={EMPTY_TEXT[tab]}
          onRefresh={reload}
        >
          {isSlotsTab ? (
            <AgendaSlotsList slots={slots} />
          ) : (
            appointments.map((row) => (
              <BookedTeleconsultoriaRowCard
                key={row.appointmentId}
                row={row}
                canStart={tab === "upcoming" && canStart(row)}
                pendingInvite={tab === "upcoming" ? pendingInviteOf(row) : undefined}
              />
            ))
          )}
        </ActivitySectionFrame>
      </Box>
    </Drawer>
  );
});
